import React from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { Search, CodepenIcon, Code, TestTube2, Rocket, Shield, Flag, Lock } from 'lucide-react';

const ProcessSection = () => {
  const { t } = useTranslation();
  
  return (
    <section id="process" className="py-16 md:py-24 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            {t('businessAppsPage.process.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('businessAppsPage.process.subtitle')}
          </p>
        </div>
        
        {/* Prozess-Timeline */}
        <div className="max-w-5xl mx-auto mb-16">
          <div className="relative">
            {/* Vertikale Linie */}
            <div className="absolute left-5 md:left-1/2 transform md:translate-x-[-50%] top-0 bottom-0 w-1 bg-blue-200 hidden md:block"></div>
            
            {/* Prozessschritte */}
            <div className="space-y-12">
              {/* Schritt 1 */}
              <div className="flex flex-col md:flex-row md:items-center group">
                <div className="md:w-1/2 md:pr-12 md:text-right order-2 md:order-1 mt-4 md:mt-0">
                  <h3 className="text-xl font-bold mb-2 text-blue-800">
                    {t('businessAppsPage.process.steps.analysis.title')}
                  </h3>
                  <p className="text-gray-600 mb-3">
                    {t('businessAppsPage.process.steps.analysis.description')}
                  </p>
                  <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                    {t('businessAppsPage.process.steps.analysis.duration')}
                  </span>
                </div>
                <div className="relative md:absolute md:left-1/2 md:transform md:translate-x-[-50%] flex items-center justify-center z-10 order-1 md:order-2">
                  <div className="h-10 w-10 rounded-full bg-blue-600 text-white flex items-center justify-center shadow-lg transition-all group-hover:scale-110">
                    <Search className="h-5 w-5" />
                  </div>
                </div>
                <div className="md:w-1/2 md:pl-12 hidden md:block order-3"></div>
              </div>
              
              {/* Schritt 2 */}
              <div className="flex flex-col md:flex-row md:items-center group">
                <div className="md:w-1/2 md:pr-12 hidden md:block order-1"></div>
                <div className="relative md:absolute md:left-1/2 md:transform md:translate-x-[-50%] flex items-center justify-center z-10 order-1 md:order-2">
                  <div className="h-10 w-10 rounded-full bg-blue-600 text-white flex items-center justify-center shadow-lg transition-all group-hover:scale-110">
                    <CodepenIcon className="h-5 w-5" />
                  </div>
                </div>
                <div className="md:w-1/2 md:pl-12 order-2 md:order-3 mt-4 md:mt-0">
                  <h3 className="text-xl font-bold mb-2 text-blue-800">
                    {t('businessAppsPage.process.steps.concept.title')}
                  </h3>
                  <p className="text-gray-600 mb-3">
                    {t('businessAppsPage.process.steps.concept.description')}
                  </p>
                  <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                    {t('businessAppsPage.process.steps.concept.duration')}
                  </span>
                </div>
              </div>
              
              {/* Schritt 3 */}
              <div className="flex flex-col md:flex-row md:items-center group">
                <div className="md:w-1/2 md:pr-12 md:text-right order-2 md:order-1 mt-4 md:mt-0">
                  <h3 className="text-xl font-bold mb-2 text-blue-800">
                    {t('businessAppsPage.process.steps.development.title')}
                  </h3>
                  <p className="text-gray-600 mb-3">
                    {t('businessAppsPage.process.steps.development.description')}
                  </p>
                  <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                    {t('businessAppsPage.process.steps.development.duration')}
                  </span>
                </div>
                <div className="relative md:absolute md:left-1/2 md:transform md:translate-x-[-50%] flex items-center justify-center z-10 order-1 md:order-2">
                  <div className="h-10 w-10 rounded-full bg-blue-600 text-white flex items-center justify-center shadow-lg transition-all group-hover:scale-110">
                    <Code className="h-5 w-5" />
                  </div>
                </div>
                <div className="md:w-1/2 md:pl-12 hidden md:block order-3"></div>
              </div>
              
              {/* Schritt 4 */}
              <div className="flex flex-col md:flex-row md:items-center group">
                <div className="md:w-1/2 md:pr-12 hidden md:block order-1"></div>
                <div className="relative md:absolute md:left-1/2 md:transform md:translate-x-[-50%] flex items-center justify-center z-10 order-1 md:order-2">
                  <div className="h-10 w-10 rounded-full bg-blue-600 text-white flex items-center justify-center shadow-lg transition-all group-hover:scale-110">
                    <TestTube2 className="h-5 w-5" />
                  </div>
                </div>
                <div className="md:w-1/2 md:pl-12 order-2 md:order-3 mt-4 md:mt-0">
                  <h3 className="text-xl font-bold mb-2 text-blue-800">
                    {t('businessAppsPage.process.steps.testing.title')}
                  </h3>
                  <p className="text-gray-600 mb-3">
                    {t('businessAppsPage.process.steps.testing.description')}
                  </p>
                  <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                    {t('businessAppsPage.process.steps.testing.duration')}
                  </span>
                </div>
              </div>
              
              {/* Schritt 5 */}
              <div className="flex flex-col md:flex-row md:items-center group">
                <div className="md:w-1/2 md:pr-12 md:text-right order-2 md:order-1 mt-4 md:mt-0">
                  <h3 className="text-xl font-bold mb-2 text-blue-800">
                    {t('businessAppsPage.process.steps.launch.title')}
                  </h3>
                  <p className="text-gray-600 mb-3">
                    {t('businessAppsPage.process.steps.launch.description')}
                  </p>
                  <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                    {t('businessAppsPage.process.steps.launch.duration')}
                  </span>
                </div>
                <div className="relative md:absolute md:left-1/2 md:transform md:translate-x-[-50%] flex items-center justify-center z-10 order-1 md:order-2">
                  <div className="h-10 w-10 rounded-full bg-blue-600 text-white flex items-center justify-center shadow-lg transition-all group-hover:scale-110">
                    <Rocket className="h-5 w-5" />
                  </div>
                </div>
                <div className="md:w-1/2 md:pl-12 hidden md:block order-3"></div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Trust-Badges */}
        <div className="flex flex-wrap justify-center items-center gap-6 max-w-4xl mx-auto">
          <div className="flex items-center bg-white shadow-sm rounded-full px-4 py-2 border border-gray-200">
            <Flag className="h-5 w-5 text-blue-600 mr-2" />
            <span className="text-gray-700">{t('businessAppsPage.process.badges.iso')}</span>
          </div>
          
          <div className="flex items-center bg-white shadow-sm rounded-full px-4 py-2 border border-gray-200">
            <CodepenIcon className="h-5 w-5 text-blue-600 mr-2" />
            <span className="text-gray-700">{t('businessAppsPage.process.badges.scrum')}</span>
          </div>
          
          <div className="flex items-center bg-white shadow-sm rounded-full px-4 py-2 border border-gray-200">
            <Shield className="h-5 w-5 text-blue-600 mr-2" />
            <span className="text-gray-700">{t('businessAppsPage.process.badges.security')}</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection; 